// src/components/admin/CategoryManagement.js
import { FolderPlusIcon, Layers, Pencil, Plus, Trash2 } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import axios from "../../utils/axiosHandler";
import { PaginatedList } from "./PaginatedList";

const CategoryManagement = ({
  setNotification,
  setActiveMenu,
  setConfirmModal,
}) => {
  // State variables for categories and parent categories
  const [categories, setCategories] = useState([]);
  const [parentCategories, setParentCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    image: null,
    parentCategory: "",
    order: 0,
  });
  const [showEditForm, setShowEditForm] = useState(false);
  const [filterByParent, setFilterByParent] = useState("");

  // Special state for parent category form
  const [showParentForm, setShowParentForm] = useState(false);
  const [parentFormData, setParentFormData] = useState({
    name: "",
    description: "",
    image: null,
    order: 0,
  });
  const [selectedParent, setSelectedParent] = useState(null);

  // Fetch categories data
  // const fetchCategories = useCallback(async () => {
  //   try {
  //     setLoading(true);
  //     const token = localStorage.getItem("token");
  //     const headers = { Authorization: `Bearer ${token}` };

  //     let url = "/api/categories";
  //     if (filterByParent) {
  //       url = `${url}?parentId=${filterByParent}`;
  //     }

  //     const response = await fetch(url, {
  //       method: "GET",
  //       headers: headers,
  //     });

  //     if (!response.ok) {
  //       throw new Error(`HTTP error! status: ${response.status}`);
  //     }

  //     const categoriesData = await response.json();
  //     setCategories(categoriesData);
  //   } catch (error) {
  //     console.error("Error fetching categories:", error);
  //     setNotification({
  //       type: "error",
  //       message: "Failed to load categories. Please try again.",
  //     });
  //   } finally {
  //     setLoading(false);
  //   }
  // }, [setNotification, filterByParent]);
  // 5. Modify fetchCategories to include sorting by order
  const fetchCategories = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      let url = "/api/categories";
      if (filterByParent) {
        url = `${url}?parentId=${filterByParent}`;
      }

      const response = await axios.get(url, {
        headers: headers,
      });

      if (response.status !== 200) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const categoriesData = await response.data;

      // Sort by order field
      categoriesData.sort((a, b) => a.order - b.order);

      setCategories(categoriesData);
    } catch (error) {
      console.error("Error fetching categories:", error);
      setNotification({
        type: "error",
        message: "Failed to load categories. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  }, [setNotification, filterByParent]);

  // Fetch parent categories
  // const fetchParentCategories = useCallback(async () => {
  //   try {
  //     const token = localStorage.getItem("token");
  //     const headers = { Authorization: `Bearer ${token}` };

  //     const response = await fetch("/api/parent-categories", {
  //       method: "GET",
  //       headers: headers,
  //     });

  //     if (!response.ok) {
  //       throw new Error(`HTTP error! status: ${response.status}`);
  //     }

  //     const data = await response.json();
  //     setParentCategories(data);
  //   } catch (error) {
  //     console.error("Error fetching parent categories:", error);
  //   }
  // }, []);
  const fetchParentCategories = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      const response = await axios.get("/api/parent-categories", {
        headers: headers,
      });

      if (response.status !== 200) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.data;

      // Sort by order field
      data.sort((a, b) => a.order - b.order);

      setParentCategories(data);
    } catch (error) {
      console.error("Error fetching parent categories:", error);
    }
  }, []);

  // Load data on component mount
  useEffect(() => {
    fetchCategories();
    fetchParentCategories();
  }, [fetchCategories, fetchParentCategories]);

  // Initialize/reset form when categories change
  useEffect(() => {
    setShowEditForm(false);
    setFormData({ name: "", description: "", image: null, parentCategory: "" });
  }, [categories.length]);

  // Update categories when filter changes
  useEffect(() => {
    fetchCategories();
  }, [filterByParent, fetchCategories]);

  // Function to switch to Add Category mode
  const handleAddCategoryClick = () => {
    setShowEditForm(true);
    setFormData({
      name: "",
      description: "",
      image: null,
      parentCategory: filterByParent || "",
    });
  };

  // Function to switch to Add Parent Category mode
  const handleAddParentCategoryClick = () => {
    setShowParentForm(true);
    setParentFormData({ name: "", description: "", image: null });
  };

  // Handle parent category filter change
  const handleParentFilterChange = (e) => {
    setFilterByParent(e.target.value);
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        const compressedImage = await compressImage(file);
        setFormData({ ...formData, image: compressedImage });
      } catch (error) {
        console.error("Error compressing image:", error);
      }
    }
  };

  const handleParentImageChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        const compressedImage = await compressImage(file);
        setParentFormData({ ...parentFormData, image: compressedImage });
      } catch (error) {
        console.error("Error compressing image:", error);
      }
    }
  };

  const handleOrderChange = (e) => {
    setFormData({ ...formData, order: parseInt(e.target.value) || 0 });
  };

  const handleParentOrderChange = (e) => {
    setParentFormData({
      ...parentFormData,
      order: parseInt(e.target.value) || 0,
    });
  };

  // reordering functionality
  const handleMoveUp = async (id, isParent = false) => {
    try {
      // Find the current item and the item above it
      const items = isParent ? parentCategories : categories;
      const currentIndex = items.findIndex((item) => item._id === id);

      if (currentIndex <= 0) return; // Already at the top

      const currentItem = items[currentIndex];
      const prevItem = items[currentIndex - 1];

      // Swap their orders
      const token = localStorage.getItem("token");
      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      };

      // Update the current item with the previous item's order
      const endpoint = isParent
        ? "/api/parent-categories/"
        : "/api/categories/";

      await axios.put(`${endpoint}${currentItem._id}/reorder`, {
        newOrder: prevItem.order,
        swapWithId: prevItem._id,
      }, {
        headers,
      });

      // Refresh data
      if (isParent) {
        await fetchParentCategories();
      } else {
        await fetchCategories();
      }

      setNotification({
        type: "success",
        message: `${isParent ? "Parent category" : "Category"
          } moved up successfully!`,
      });

      setTimeout(() => setNotification(null), 3000);
    } catch (error) {
      console.error(
        `Error reordering ${isParent ? "parent category" : "category"}:`,
        error
      );
      setNotification({
        type: "error",
        message: `Failed to reorder ${isParent ? "parent category" : "category"
          }`,
      });

      setTimeout(() => setNotification(null), 3000);
    }
  };

  const handleMoveDown = async (id, isParent = false) => {
    try {
      // Find the current item and the item below it
      const items = isParent ? parentCategories : categories;
      const currentIndex = items.findIndex((item) => item._id === id);

      if (currentIndex >= items.length - 1) return; // Already at the bottom

      const currentItem = items[currentIndex];
      const nextItem = items[currentIndex + 1];

      // Swap their orders
      const token = localStorage.getItem("token");
      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      };

      // Update the current item with the next item's order
      const endpoint = isParent
        ? "/api/parent-categories/"
        : "/api/categories/";

      await axios.put(`${endpoint}${currentItem._id}/reorder`, {
        newOrder: nextItem.order,
        swapWithId: nextItem._id,
      }, {
        headers,
      });

      // Refresh data
      if (isParent) {
        await fetchParentCategories();
      } else {
        await fetchCategories();
      }

      setNotification({
        type: "success",
        message: `${isParent ? "Parent category" : "Category"
          } moved down successfully!`,
      });

      setTimeout(() => setNotification(null), 3000);
    } catch (error) {
      console.error(
        `Error reordering ${isParent ? "parent category" : "category"}:`,
        error
      );
      setNotification({
        type: "error",
        message: `Failed to reorder ${isParent ? "parent category" : "category"
          }`,
      });

      setTimeout(() => setNotification(null), 3000);
    }
  };

  const compressImage = async (file, maxWidth = 800, maxHeight = 800) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (e) => {
        const img = new Image();
        img.src = e.target.result;
        img.onload = () => {
          const canvas = document.createElement("canvas");
          let width = img.width;
          let height = img.height;

          if (width > height) {
            if (width > maxWidth) {
              height = Math.round((height * maxWidth) / width);
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width = Math.round((width * maxHeight) / height);
              height = maxHeight;
            }
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");
          // Clear canvas with transparent background
          ctx.clearRect(0, 0, width, height);
          ctx.drawImage(img, 0, 0, width, height);
          resolve(canvas.toDataURL("image/png"));
        };
      };
    });
  };

  // Handle category form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { ...formData };
      const token = localStorage.getItem("token");
      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      };

      const categoryData = {
        name: data.name,
        description: data.description,
        image: data.image,
        parentCategory: data.parentCategory || null,
        order: data.order || 0,
      };

      let response;

      if (selectedItem) {
        response = await axios.put(`/api/categories/${selectedItem._id}`, categoryData, {
          headers,
        });
      } else {
        response = await axios.post("/api/categories", categoryData, {
          headers,
        });
      }

      const responseData = await response.data;

      if (response.status !== 200 && response.status !== 201) {
        throw new Error(
          responseData.error ||
          `Failed to ${selectedItem ? "update" : "create"} category`
        );
      }

      await fetchCategories(); // Refresh data after submission
      setFormData({
        name: "",
        description: "",
        image: null,
        parentCategory: filterByParent || "",
      });
      setSelectedItem(null);
      setShowEditForm(false);

      setNotification({
        type: "success",
        message: selectedItem
          ? "Category updated successfully!"
          : "Category added successfully!",
      });

      setTimeout(() => setNotification(null), 3000);
    } catch (error) {
      console.error(
        `Error ${selectedItem ? "updating" : "creating"} category:`,
        error
      );
      setNotification({
        type: "error",
        message:
          error.message ||
          `Failed to ${selectedItem ? "update" : "add"} category`,
      });

      setTimeout(() => setNotification(null), 3000);
    }
  };

  // Handle parent category form submission
  const handleParentSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { ...parentFormData };
      const token = localStorage.getItem("token");
      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      };

      const parentData = {
        name: data.name,
        description: data.description,
        image: data.image,
        order: data.order || 0,
      };

      let response;

      if (selectedParent) {
        response = await axios.put(`/api/parent-categories/${selectedParent._id}`, parentData, {
          headers,
        });
      } else {
        response = await axios.post("/api/parent-categories", parentData, {
          headers,
        });
      }

      const responseData = await response.data;

      if (response.status !== 200 && response.status !== 201) {
        throw new Error(
          responseData.error ||
          `Failed to ${selectedParent ? "update" : "create"} parent category`
        );
      }

      await fetchParentCategories(); // Refresh parent categories
      setParentFormData({ name: "", description: "", image: null });
      setSelectedParent(null);
      setShowParentForm(false);

      setNotification({
        type: "success",
        message: selectedParent
          ? "Parent category updated successfully!"
          : "Parent category added successfully!",
      });

      setTimeout(() => setNotification(null), 3000);
    } catch (error) {
      console.error(
        `Error ${selectedParent ? "updating" : "creating"} parent category:`,
        error
      );
      setNotification({
        type: "error",
        message:
          error.message ||
          `Failed to ${selectedParent ? "update" : "add"} parent category`,
      });

      setTimeout(() => setNotification(null), 3000);
    }
  };

  // Handle editing a category
  const handleEdit = (item) => {
    const editableItem = {
      ...item,
      image: item.image?.data || item.image,
      parentCategory: item.parentCategory?._id || item.parentCategory || "",
      order: item.order || 0,
    };

    setSelectedItem(item);
    setFormData(editableItem);
    setShowEditForm(true);
  };

  // Handle editing a parent category
  const handleEditParent = (parent) => {
    const editableParent = {
      ...parent,
      image: parent.image?.data || parent.image,
      order: parent.order || 0,
    };

    setSelectedParent(parent);
    setParentFormData(editableParent);
    setShowParentForm(true);
  };

  // Handle category deletion
  const handleDelete = (categoryId) => {
    const category = categories.find((c) => c._id === categoryId);

    setConfirmModal({
      show: true,
      title: "Delete Category",
      message: `Are you sure you want to delete "${category?.name}"? This action cannot be undone.`,
      onConfirm: async () => {
        try {
          const token = localStorage.getItem("token");
          const response = await axios.delete(`/api/categories/${categoryId}`, {
            headers: { Authorization: `Bearer ${token}` },
          });

          const data = await response.data;

          if (response.status !== 200) {
            throw new Error(data.error || `Failed to delete category`);
          }

          await fetchCategories(); // Refresh data after deletion

          setNotification({
            type: "success",
            message: data.message || `Category deleted successfully!`,
          });

          setTimeout(() => setNotification(null), 3000);
        } catch (error) {
          console.error(`Error deleting category:`, error);

          setNotification({
            type: "error",
            message: error.message || `Failed to delete category`,
          });

          setTimeout(() => setNotification(null), 3000);
        }
      },
    });
  };

  // Handle parent category deletion
  const handleDeleteParent = (parentId) => {
    const parent = parentCategories.find((p) => p._id === parentId);

    setConfirmModal({
      show: true,
      title: "Delete Parent Category",
      message: `Are you sure you want to delete "${parent?.name}"? This action cannot be undone.`,
      onConfirm: async () => {
        try {
          const token = localStorage.getItem("token");
          const response = await axios.delete(`/api/parent-categories/${parentId}`, {
            headers: { Authorization: `Bearer ${token}` },
          });

          const data = await response.data;

          if (response.status !== 200) {
            throw new Error(data.error || `Failed to delete parent category`);
          }

          await fetchParentCategories(); // Refresh parent categories

          setNotification({
            type: "success",
            message: data.message || `Parent category deleted successfully!`,
          });

          setTimeout(() => setNotification(null), 3000);
        } catch (error) {
          console.error(`Error deleting parent category:`, error);

          setNotification({
            type: "error",
            message: error.message || `Failed to delete parent category`,
          });

          setTimeout(() => setNotification(null), 3000);
        }
      },
    });
  };

  if (loading) {
    return (
      <div className="fixed inset-0 flex flex-col items-center justify-center">
        <img src="../images/loading.gif" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-2xl font-semibold">Category Management</h3>

        <div className="flex space-x-4">
          {/* Add Parent Category button */}
          {!showEditForm && !showParentForm && (
            <button
              onClick={handleAddParentCategoryClick}
              className="bg-purple-500 text-white px-4 py-2 rounded hover:bg-purple-600 flex items-center"
            >
              <Layers className="h-5 w-5 mr-2" />
              Add Parent Category
            </button>
          )}

          {/* Add Category button */}
          {!showEditForm && !showParentForm && (
            <button
              onClick={handleAddCategoryClick}
              className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 flex items-center"
            >
              <FolderPlusIcon className="h-5 w-5 mr-2" />
              Add Category
            </button>
          )}
        </div>
      </div>

      {/* Parent Category Section - always visible unless in edit mode */}
      {!showEditForm && !showParentForm && (
        <div className="mb-8">
          <div className="flex justify-between items-center mb-4">
            <h4 className="text-xl font-medium">Parent Categories</h4>
          </div>

          {parentCategories.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {parentCategories.map((parent) => (
                <div
                  key={parent._id}
                  className="bg-white rounded-lg shadow p-4 flex flex-col h-full"
                >
                  <div className="flex justify-between items-start mb-2">
                    <h5 className="font-semibold text-lg">{parent.name}</h5>
                    <div className="flex space-x-2">
                      <button
                        style={{ boxShadow: "none" }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMoveUp(parent._id, true);
                        }}
                        className="p-1 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors"
                        title="Move up"
                      >
                        ⬆️
                      </button>
                      <button
                        style={{ boxShadow: "none" }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMoveDown(parent._id, true);
                        }}
                        className="p-1 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors"
                        title="Move down"
                      >
                        ⬇️
                      </button>
                      <span className="text-xs text-gray-500 self-center ml-1">
                        Order: {parent.order}
                      </span>

                      <button
                        onClick={() => handleEditParent(parent)}
                        style={{ boxShadow: "none" }}
                        className="p-1 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded-full transition-colors"
                        title="Edit parent category"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteParent(parent._id)}
                        style={{ boxShadow: "none" }}
                        className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-full transition-colors"
                        title="Delete parent category"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center mb-3">
                    {parent.image && (
                      <img
                        src={
                          parent.image.path
                            ? `/api${parent.image.path}`
                            : parent.image.data || parent.image
                        }
                        alt={parent.name}
                        className="w-16 h-16 object-cover mr-3 rounded"
                      />
                    )}

                    <p className="text-sm text-gray-600 flex-grow">
                      {parent.description}
                    </p>
                  </div>

                  <button
                    onClick={() => setFilterByParent(parent._id)}
                    className="mt-auto bg-gray-100 text-gray-800 px-3 py-1 rounded text-sm hover:bg-gray-200 transition-colors"
                  >
                    View Categories
                  </button>
                </div>
              ))}

              {/* Add option for top-level categories */}
              <div className="bg-white rounded-lg shadow p-4 flex flex-col h-full border-2 border-dashed border-gray-300">
                <h5 className="font-semibold text-lg">Top-Level Categories</h5>
                <p className="text-sm text-gray-600 mb-4">
                  Categories with no parent
                </p>
                <button
                  onClick={() => setFilterByParent("none")}
                  className="mt-auto bg-gray-100 text-gray-800 px-3 py-1 rounded text-sm hover:bg-gray-200 transition-colors"
                >
                  View Categories
                </button>
              </div>

              {/* Option to view all categories */}
              <div className="bg-white rounded-lg shadow p-4 flex flex-col h-full border-2 border-dashed border-gray-300">
                <h5 className="font-semibold text-lg">All Categories</h5>
                <p className="text-sm text-gray-600 mb-4">
                  View all categories regardless of parent
                </p>
                <button
                  onClick={() => setFilterByParent("")}
                  className="mt-auto bg-gray-100 text-gray-800 px-3 py-1 rounded text-sm hover:bg-gray-200 transition-colors"
                >
                  View All Categories
                </button>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow p-8 text-center">
              <p className="text-gray-600 mb-4">
                No parent categories found. Create your first parent category!
              </p>
              <button
                onClick={handleAddParentCategoryClick}
                className="bg-purple-500 text-white px-4 py-2 rounded hover:bg-purple-600 inline-flex items-center"
              >
                <Plus className="h-5 w-5 mr-2" />
                Add Parent Category
              </button>
            </div>
          )}
        </div>
      )}

      {/* Category Filter and List */}
      {!showEditForm && !showParentForm && (
        <>
          <div className="flex justify-between items-center mb-4">
            <h4 className="text-xl font-medium">
              {filterByParent
                ? filterByParent === "none"
                  ? "Top-Level Categories"
                  : `Categories for ${parentCategories.find((p) => p._id === filterByParent)
                    ?.name || "Selected Parent"
                  }`
                : "All Categories"}
            </h4>

            <div className="flex items-center space-x-4">
              <div>
                <label htmlFor="parentFilter" className="mr-2 text-gray-700">
                  Filter by Parent:
                </label>
                <select
                  id="parentFilter"
                  value={filterByParent}
                  onChange={handleParentFilterChange}
                  className="px-3 py-2 border rounded"
                >
                  <option value="">All Categories</option>
                  <option value="none">Top-Level Categories (No Parent)</option>
                  {parentCategories.map((parent) => (
                    <option key={parent._id} value={parent._id}>
                      {parent.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Categories List */}
          {categories.length > 0 ? (
            <PaginatedList
              items={categories}
              renderItem={(category) => (
                <div
                  key={category._id}
                  className="flex justify-between items-center p-4 bg-white rounded-lg shadow"
                  style={{ marginBottom: 10 + "px" }}
                >
                  {category.image && (
                    <img
                      src={
                        category.image.path
                          ? `/api${category.image.path}`
                          : category.image.url
                            ? category.image.url
                            : category.image.data || category.image
                      }
                      alt={category.name}
                      className="w-24 h-24 object-cover mr-4 rounded"
                    />
                  )}
                  <div className="flex-grow">
                    <h4 className="font-semibold">{category.name}</h4>
                    <p className="text-gray-600">{category.description}</p>
                    {category.parentCategory && (
                      <p className="text-gray-500 text-sm">
                        <span className="inline-flex items-center">
                          <Layers className="h-4 w-4 mr-1" />
                          Parent: {category.parentCategory.name}
                        </span>
                      </p>
                    )}
                  </div>
                  <div className="flex space-x-2">
                    {/* <button
                      onClick={() => handleMoveUp(category._id)}
                      className="p-2 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded-full transition-colors"
                      title="Move up"
                    >
                      ⬆️
                    </button>
                    <button
                      onClick={() => handleMoveDown(category._id)}
                      className="p-2 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded-full transition-colors"
                      title="Move down"
                    >
                      ⬇️
                    </button> */}
                    <span className="text-xs text-gray-500 self-center ml-1">
                      Order: {category.order}
                    </span>

                    <button
                      onClick={() => handleEdit(category)}
                      style={{ boxShadow: "none" }}
                      className="p-2 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded-full transition-colors"
                      title="Edit category"
                    >
                      <Pencil className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(category._id)}
                      style={{ boxShadow: "none" }}
                      className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-full transition-colors"
                      title="Delete category"
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              )}
            />
          ) : (
            <div className="bg-white rounded-lg shadow p-8 text-center">
              <p className="text-gray-600 mb-4">
                {filterByParent
                  ? `No categories found for the selected parent.`
                  : `No categories found. Create your first category!`}
              </p>
              <button
                onClick={handleAddCategoryClick}
                className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 inline-flex items-center"
              >
                <Plus className="h-5 w-5 mr-2" />
                Add Category
              </button>
            </div>
          )}
        </>
      )}

      {/* Parent Category Form */}
      {showParentForm && (
        <form
          onSubmit={handleParentSubmit}
          className="space-y-4 bg-white p-6 rounded-lg shadow"
        >
          <div className="flex justify-between items-center mb-4">
            <h4 className="text-lg font-medium">
              {selectedParent ? "Update" : "Add"} Parent Category
            </h4>

            <button
              type="button"
              onClick={() => {
                setShowParentForm(false);
                setParentFormData({ name: "", description: "", image: null });
                setSelectedParent(null);
              }}
              className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
            >
              Cancel
            </button>
          </div>

          <div>
            <label className="block text-gray-700 mb-2">Name</label>
            <input
              type="text"
              value={parentFormData.name || ""}
              onChange={(e) =>
                setParentFormData({ ...parentFormData, name: e.target.value })
              }
              className="w-full px-3 py-2 border rounded"
              required
            />
          </div>
          <div>
            <label className="block text-gray-700 mb-2">Description</label>
            <textarea
              value={parentFormData.description || ""}
              onChange={(e) =>
                setParentFormData({
                  ...parentFormData,
                  description: e.target.value,
                })
              }
              className="w-full px-3 py-2 border rounded"
              rows="3"
            />
          </div>
          <div>
            <label className="block text-gray-700 mb-2">Display Order</label>
            <input
              type="number"
              value={parentFormData.order || 0}
              onChange={handleParentOrderChange}
              className="w-full px-3 py-2 border rounded"
              min="0"
            />
            <small className="text-gray-500">Lower numbers appear first</small>
          </div>
          <div>
            <label className="block text-gray-700 mb-2">Image</label>
            <input
              type="file"
              onChange={handleParentImageChange}
              accept="image/*"
              className="w-full"
            />
            {/* {parentFormData.image && (
              <img
                src={
                  typeof parentFormData.image === "string"
                    ? parentFormData.image
                    : parentFormData.image.data
                }
                alt="Preview"
                className="mt-2 h-32 object-contain"
              />
            )} */}
            {parentFormData.image && (
              <img
                src={
                  typeof parentFormData.image === "object" &&
                    parentFormData.image.path
                    ? `/api${parentFormData.image.path}`
                    : typeof parentFormData.image === "object" &&
                      parentFormData.image.url
                      ? parentFormData.image.url
                      : typeof parentFormData.image === "string"
                        ? parentFormData.image
                        : parentFormData.image.data
                }
                alt="Preview"
                className="mt-2 h-32 object-contain"
              />
            )}
          </div>
          <button
            type="submit"
            className="bg-purple-500 text-white px-4 py-2 rounded hover:bg-purple-600"
          >
            {selectedParent ? "Update" : "Add"} Parent Category
          </button>
        </form>
      )}

      {/* Regular Category Form */}
      {showEditForm && (
        <form
          onSubmit={handleSubmit}
          className="space-y-4 bg-white p-6 rounded-lg shadow"
        >
          <div className="flex justify-between items-center mb-4">
            <h4 className="text-lg font-medium">
              {formData._id ? "Update" : "Add"} Category
            </h4>

            <button
              type="button"
              onClick={() => {
                setShowEditForm(false);
                setFormData({
                  name: "",
                  description: "",
                  image: null,
                  parentCategory: filterByParent || "",
                });
                setSelectedItem(null);
              }}
              className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
            >
              Cancel
            </button>
          </div>

          <div>
            <label className="block text-gray-700 mb-2">Name</label>
            <input
              type="text"
              value={formData.name || ""}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              className="w-full px-3 py-2 border rounded"
              required
            />
          </div>
          <div>
            <label className="block text-gray-700 mb-2">Description</label>
            <textarea
              value={formData.description || ""}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              className="w-full px-3 py-2 border rounded"
              rows="3"
            />
          </div>
          <div>
            <label className="block text-gray-700 mb-2">Display Order</label>
            <input
              type="number"
              value={formData.order || 0}
              onChange={handleOrderChange}
              className="w-full px-3 py-2 border rounded"
              min="0"
            />
            <small className="text-gray-500">Lower numbers appear first</small>
          </div>
          <div>
            <label className="block text-gray-700 mb-2">
              Parent Category (Optional)
            </label>
            <select
              value={formData.parentCategory || ""}
              onChange={(e) =>
                setFormData({ ...formData, parentCategory: e.target.value })
              }
              className="w-full px-3 py-2 border rounded"
            >
              <option value="">No Parent (Top Level Category)</option>
              {parentCategories.map((parent) => (
                <option key={parent._id} value={parent._id}>
                  {parent.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-gray-700 mb-2">Image</label>
            <input
              type="file"
              onChange={handleImageChange}
              accept="image/*"
              className="w-full"
            />
            {formData.image && (
              <img
                src={
                  typeof formData.image === "object" && formData.image.path
                    ? `/api${formData.image.path}`
                    : typeof formData.image === "object" && formData.image.url
                      ? formData.image.url
                      : typeof formData.image === "string"
                        ? formData.image
                        : formData.image.data
                }
                alt="Preview"
                className="mt-2 h-32 object-contain"
              />
            )}
          </div>
          <button
            type="submit"
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
          >
            {formData._id ? "Update" : "Add"} Category
          </button>
        </form>
      )}
    </div>
  );
};

export default CategoryManagement;
